/**
 * What the CHM's bare ground says about its heights, under the map: the verdict in one line, then
 * every figure of `chm_quality.json` in one table with the limit it is judged by, every square the
 * model was checked on, and how the measurement works.
 */
import { Accordion, Alert, Badge, Stack, Table, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useFormat } from '@/components/useFormat';
import { brand } from '@/theme/theme';
import { Section } from '../trees/Section';
import type { ChmQuality } from './chmQuality';

const COLOR: Record<ChmQuality['verdict'], string> = {
  constant: 'forest',
  plane: 'forest',
  warped: 'yellow',
  insufficient_ground: 'gray',
  unavailable: 'gray',
};

export function ChmQualitySection({ quality }: { quality: ChmQuality }) {
  const { t } = useTranslation('reports');
  const { number } = useFormat();
  const q = quality;
  const limit = q.limits;
  const m = (v: number) => number(v, 2);
  const signed = (v: number) => (v > 0 ? `+${m(v)}` : m(v));
  const ha = (pixels: number) => number(pixels / 10_000, 2);
  const measured = q.verdict === 'constant' || q.verdict === 'plane' || q.verdict === 'warped';
  const tilt = q.tiltXPer100M !== null && q.tiltYPer100M !== null ? `${signed(q.tiltXPer100M)} / ${signed(q.tiltYPer100M)}` : null;

  // One row per figure of chm_quality.json, with the limit it is judged by where the report names one.
  type Row = { key: string; value: string; limit?: string; ok?: boolean; neutral?: boolean };
  const atMost = (v: number, lim: number | null, unit = ' m') =>
    lim === null ? {} : { limit: `≤ ${m(lim)}${unit}`, ok: v <= lim };
  const atLeast = (v: number, lim: number | null, shown: string) => (lim === null ? {} : { limit: `≥ ${shown}`, ok: v >= lim });
  const rows: Row[] = [];
  const push = (row: Row | false | null) => row && rows.push(row);
  push(q.offsetM !== null && { key: 'offset', value: `${signed(q.offsetM)} m` });
  push(q.modeM !== null && { key: 'mode', value: `${signed(q.modeM)} m` });
  push(q.groundPixels !== null && {
    key: 'ground',
    value: q.groundShare !== null ? `${ha(q.groundPixels)} ha (${number(q.groundShare * 100, 1)} %)` : `${ha(q.groundPixels)} ha`,
    ...atLeast(q.groundPixels, limit.groundPixels, `${number(limit.groundPixels ?? 0)} m²`),
  });
  push(q.areaHa !== null && { key: 'area', value: `${number(q.areaHa, 1)} ha` });
  push(q.madM !== null && { key: 'mad', value: `${m(q.madM)} m`, ...atMost(q.madM, limit.madM) });
  push(q.planeMadM !== null && { key: 'planeMad', value: `${m(q.planeMadM)} m`, ...atMost(q.planeMadM, limit.madM) });
  push(q.tileResidualMaxM !== null && { key: 'worstTile', value: `${m(q.tileResidualMaxM)} m`, ...atMost(q.tileResidualMaxM, limit.tileResidualM) });
  push(q.groundTiles !== null && {
    key: 'tiles',
    value: number(q.groundTiles),
    ...atLeast(q.groundTiles, limit.planeTiles, String(limit.planeTiles)),
    neutral: limit.planeTiles !== null && q.groundTiles < limit.planeTiles,
  });
  push(tilt !== null && limit.planeTiles !== null && (q.groundTiles ?? 0) >= limit.planeTiles && { key: 'tilt', value: `${tilt} m/100 m` });
  push(measured && {
    key: 'correction',
    value: q.correction ? t(`chmQuality.correction.${q.correction.kind}`) + ` (${signed(-q.correction.offsetM)} m)` : t('chmQuality.correction.none'),
  });
  const shown = (v: number | null, f: (x: number) => string) => (v === null ? '?' : f(v));

  const offset = q.offsetM ?? 0;
  const tiles = [...q.tiles]
    .map((tile) => ({ ...tile, counted: limit.tileGroundPixels === null ? null : tile.groundPixels >= limit.tileGroundPixels, delta: tile.medianM - offset }))
    .sort((a, b) => Number(b.counted) - Number(a.counted) || Math.abs(b.delta) - Math.abs(a.delta));

  return (
    <Section title={t('chmQuality.title')}>
      <Stack gap="md" data-testid="chm-quality">
        <Alert color={COLOR[q.verdict]} title={t(`chmQuality.verdicts.${q.verdict}`)} data-testid="chm-quality-verdict" data-verdict={q.verdict}>
          {!measured && <Text fz={14}>{q.reason}</Text>}
        </Alert>

        {rows.length > 1 && (
          <Table.ScrollContainer minWidth={560}>
            <Table data-testid="chm-quality-table">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('chmQuality.table.item')}</Table.Th>
                  <Table.Th ta="right">{t('chmQuality.table.value')}</Table.Th>
                  <Table.Th ta="right">{t('chmQuality.table.limit')}</Table.Th>
                  <Table.Th ta="right">{t('chmQuality.table.result')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((r) => (
                  <Table.Tr key={r.key} data-testid={`chm-quality-row-${r.key}`}>
                    <Table.Td>
                      <Text fz={14}>{t(`chmQuality.table.rows.${r.key}`)}</Text>
                      <Text fz={12} c={brand.textMuted}>
                        {t(`chmQuality.table.hints.${r.key}`, { size: number(q.tileM ?? 200), pixels: shown(limit.tileGroundPixels, (x) => number(x)) })}
                      </Text>
                    </Table.Td>
                    <Table.Td ta="right" style={{ whiteSpace: 'nowrap' }}>
                      {r.value}
                    </Table.Td>
                    <Table.Td ta="right" style={{ whiteSpace: 'nowrap' }}>
                      {r.limit ?? '–'}
                    </Table.Td>
                    <Table.Td ta="right">
                      {r.ok !== undefined && (
                        <Badge color={r.ok ? 'forest' : r.neutral ? 'gray' : 'red'} variant="light">
                          {t(r.ok ? 'chmQuality.table.ok' : r.neutral ? 'chmQuality.table.noTilt' : 'chmQuality.table.fail')}
                        </Badge>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}

        <Accordion variant="separated" radius="lg" multiple>
          {tiles.length > 0 && (
            <Accordion.Item value="tiles">
              <Accordion.Control data-testid="chm-quality-tiles-toggle">
                {t('chmQuality.tiles.title', { count: tiles.length, counted: tiles.filter((x) => x.counted).length, size: number(q.tileM ?? 200) })}
              </Accordion.Control>
              <Accordion.Panel>
                <Table.ScrollContainer minWidth={560}>
                  <Table data-testid="chm-quality-tiles">
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>{t('chmQuality.tiles.position', { epsg: q.epsg ?? '?' })}</Table.Th>
                        <Table.Th ta="right">{t('chmQuality.tiles.ground')}</Table.Th>
                        <Table.Th ta="right">{t('chmQuality.tiles.median')}</Table.Th>
                        <Table.Th ta="right">{t('chmQuality.tiles.delta')}</Table.Th>
                        <Table.Th ta="right">{t('chmQuality.tiles.state')}</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {tiles.map((x) => (
                        <Table.Tr key={`${x.x}/${x.y}`}>
                          <Table.Td>
                            {number(x.x)} / {number(x.y)}
                          </Table.Td>
                          <Table.Td ta="right">{number(x.groundPixels)} m²</Table.Td>
                          <Table.Td ta="right">{signed(x.medianM)} m</Table.Td>
                          <Table.Td ta="right" c={x.counted && limit.tileResidualM !== null && Math.abs(x.delta) > limit.tileResidualM ? 'red' : undefined}>
                            {signed(x.delta)} m
                          </Table.Td>
                          <Table.Td ta="right">
                            <Text fz={13} c={x.counted ? undefined : brand.textMuted}>
                              {x.counted === null ? '–' : t(x.counted ? 'chmQuality.tiles.counted' : 'chmQuality.tiles.thin')}
                            </Text>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
              </Accordion.Panel>
            </Accordion.Item>
          )}
          <Accordion.Item value="method">
            <Accordion.Control>{t('chmQuality.method.title')}</Accordion.Control>
            <Accordion.Panel>
              <Stack gap={6}>
                {(['ground', 'offset', 'tiles', 'verdict'] as const).map((k) => (
                  <Text key={k} fz={14}>
                    {t(`chmQuality.method.${k}`, {
                      mad: shown(limit.madM, m),
                      tile: shown(limit.tileResidualM, m),
                      pixels: shown(limit.tileGroundPixels, (x) => number(x)),
                      planeTiles: shown(limit.planeTiles, String),
                      size: number(q.tileM ?? 200),
                    })}
                  </Text>
                ))}
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      </Stack>
    </Section>
  );
}
